import React, { useEffect, useState } from 'react';
import { Button, Tooltip, message } from 'antd';
import { CloseOutlined, CopyOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from '../../shared/i18n';
import { userPath } from '../../shared/config/paths';
import { CampaignMotif, themeVars } from '../../shared/campaign/campaignThemes';
import couponService from '../services/couponService';
import { setPendingCoupon, withCoupon } from '../utils/pendingCoupon';
import { couponUsePath } from './CouponTicket';
import { offerText } from '../../shared/campaign/TicketValue';

const ROTATE_MS = 8000;

/** Banner line: the admin's banner text, else the coupon value — always localized by the console. */
const offerOf = (c, t) => c.banner || offerText(c.codes?.[0] || c.coupons?.[0], t);

const DISMISS_KEY = 'yeap.campaignDismissed';
const CACHE_MS = 30000;
let cache = { at: 0, data: null, p: null };

const readDismissed = () => {
    try {
        return JSON.parse(localStorage.getItem(DISMISS_KEY) || '[]') || [];
    } catch (e) {
        return [];
    }
};

/** Live campaigns for me (shared by dashboard banner, cart strip, My coupons; refetched at most every 30 s). */
export const useActiveCampaigns = () => {
    const [list, setList] = useState(cache.data || []);
    const [at, setAt] = useState(cache.at || Date.now());
    useEffect(() => {
        let alive = true;
        if (cache.data && Date.now() - cache.at < CACHE_MS) {
            setList(cache.data);
            setAt(cache.at);
            return undefined;
        }
        cache.p = cache.p || couponService.activeCampaigns().then((r) => {
            cache = { at: Date.now(), data: r?.success ? (r.data || []) : [], p: null };
            return cache;
        });
        cache.p.then((c) => { if (alive) { setList(c.data); setAt(c.at); } });
        return () => { alive = false; };
    }, []);
    return [list, at];
};

/** Countdown from the server's seconds-left (ticks every minute). */
const useEndsIn = (campaign, fetchedAt) => {
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(id);
    }, []);
    return Math.max(0, (campaign?.ends_in || 0) - Math.floor((now - fetchedAt) / 1000));
};

export const endsInText = (secs, t) => {
    if (secs >= 2 * 86400) return t('Ends in {{n}} days', { n: Math.floor(secs / 86400) });
    if (secs >= 86400) return t('Ends in 1 day');
    if (secs >= 3600) return t('Ends in {{n}} hours', { n: Math.floor(secs / 3600) });
    return t('Ends in {{n}} minutes', { n: Math.max(1, Math.floor(secs / 60)) });
};

const copy = async (code, t) => {
    try {
        await navigator.clipboard.writeText(code);
        message.success(t('Copied'));
    } catch (e) {
        message.info(code);
    }
};

/** "Use now": code campaign → purchase page with the code; wallet campaign → my campaign coupon's page. */
const targetOf = (c) => {
    const code = c.codes?.[0];
    if (code) return { to: withCoupon(code.use_link || c.use_link || '/dashboard', code.code), code: code.code };
    const mine = c.coupons?.[0];
    if (mine) return { to: couponUsePath(mine), code: mine.code };
    return { to: c.use_link || '/dashboard', code: null };
};

/** One themed banner (also used as the admin preview: pass `preview`). */
export const CampaignBannerView = ({ c, fetchedAt = Date.now(), onClose, preview = false, pager = null }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const secs = useEndsIn(c, fetchedAt);
    const code = c.codes?.[0];
    const go = () => {
        if (preview) return;
        const target = targetOf(c);
        if (target.code) setPendingCoupon(target.code);
        navigate(userPath(target.to));
    };
    return (
        <div className={`cpb cpb--${c.theme}`} style={themeVars(c.theme)} role="region" aria-label={c.name}>
            <CampaignMotif theme={c.theme} size={96} className="cpb-motif" />
            <div className="cpb-main">
                <span className="cpb-name"><CampaignMotif theme={c.theme} size={16} />{c.name}</span>
                <span className="cpb-offer">{offerOf(c, t)}</span>
                {!code && c.mode === 'wallet' ? (
                    <span className="cpb-sub">
                        {preview ? t('Your coupon is in My coupons') : <Link className="cpb-link" to={userPath('/billing/coupons?tab=campaign')}>{t('Your coupon is in My coupons')}</Link>}
                    </span>
                ) : null}
            </div>
            <div className="cpb-side">
                <span className="cpb-count">{endsInText(secs, t)}</span>
                {code ? (
                    <span className="cpb-code">
                        {code.code}
                        <Tooltip title={t('Copy')}>
                            <Button size="small" type="text" className="cpb-copy" icon={<CopyOutlined />} onClick={() => copy(code.code, t)} aria-label={t('Copy')} />
                        </Tooltip>
                    </span>
                ) : null}
                <Button className="cpb-cta" onClick={go}>{t('Use now')}</Button>
            </div>
            {onClose ? (
                <button type="button" className="cpb-close" onClick={onClose} aria-label={t('Close')}><CloseOutlined /></button>
            ) : null}
            {pager}
        </div>
    );
};

/** Dashboard: dismissible themed banner per live campaign (dismissal remembered in this browser). */
/**
 * Dashboard: one banner slot. Several live campaigns rotate (every 8 s, paused on hover / focus) with small dots;
 * each campaign is dismissible on its own (remembered in this browser).
 */
const CampaignBanner = () => {
    const { t } = useTranslation();
    const [list, at] = useActiveCampaigns();
    const [dismissed, setDismissed] = useState(readDismissed);
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const shown = list.filter((c) => !dismissed.includes(c.id));
    const n = shown.length;
    useEffect(() => {
        if (n < 2 || paused) return undefined;
        const id = setInterval(() => setIdx((i) => (i + 1) % n), ROTATE_MS);
        return () => clearInterval(id);
    }, [n, paused]);
    const close = (id) => {
        const next = [...dismissed.filter((x) => list.some((c) => c.id === x)), id];
        setDismissed(next);
        try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch (e) { /* storage blocked */ }
    };
    if (!n) return null;
    const cur = shown[idx % n];
    const pager = n > 1 ? (
        <div className="cpb-dots" role="tablist" aria-label={t('Campaigns')}>
            {shown.map((c, i) => (
                <button key={c.id} type="button" role="tab" aria-selected={i === idx % n} aria-label={c.name}
                    className={`cpb-dot${i === idx % n ? ' is-on' : ''}`} onClick={() => setIdx(i)} />
            ))}
        </div>
    ) : null;
    return (
        <div onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} onFocus={() => setPaused(true)} onBlur={() => setPaused(false)}>
            <CampaignBannerView key={cur.id} c={cur} fetchedAt={at} onClose={() => close(cur.id)} pager={pager} />
        </div>
    );
};

/** Cart: one compact line per live campaign; code campaigns get an "Apply" button. */
export const CampaignStrip = ({ onApply, appliedCode }) => {
    const { t } = useTranslation();
    const [list, at] = useActiveCampaigns();
    const [now] = useState(Date.now());
    if (!list.length) return null;
    return (
        <>
            {list.slice(0, 2).map((c) => {
                const code = c.codes?.[0];
                const secs = Math.max(0, (c.ends_in || 0) - Math.floor((now - at) / 1000));
                if (code && appliedCode && appliedCode.toUpperCase() === code.code) return null;
                return (
                    <div key={c.id} className="cpb-compact" style={themeVars(c.theme)} role="note">
                        <CampaignMotif theme={c.theme} size={22} />
                        <span className="cpb-c-text">
                            <b>{c.name}</b>
                            {offerOf(c, t)}
                            {' · '}{endsInText(secs, t)}
                            {!code ? ` · ${t('Pick it from My coupons')}` : ''}
                        </span>
                        {code ? (
                            <>
                                <span className="cpb-code">{code.code}</span>
                                <Button size="small" className="cpb-cta" onClick={() => onApply?.(code.code)}>{t('Apply')}</Button>
                            </>
                        ) : null}
                    </div>
                );
            })}
        </>
    );
};

export default CampaignBanner;
